import { computed, onBeforeUnmount, ref, unref, watch, type Ref } from "vue";
import { AgentTaskStatus } from "~/enums";
import {
  buildQuery,
  getWorkbenchLoadError,
  normalizeOptionalString,
} from "~/utils/agent-workbench";
import type {
  AgentTask,
  AgentTraceLog,
  TaskCacheItem,
  TraceLogCacheItem,
} from "~/types/agent-workbench";

type UseAgentWorkbenchOptions = {
  traceId: Ref<string | undefined>;
  sessionId: Ref<string | undefined>;
  userId: Ref<string | undefined>;
};

type LoadOptions = {
  force?: boolean;
};

const ACTIVE_TASK_STATUSES = new Set<AgentTaskStatus>([
  AgentTaskStatus.CREATED,
  AgentTaskStatus.PLANNING,
  AgentTaskStatus.EXECUTING,
]);

const TASK_REFRESH_INTERVAL_MS = 4000;

const createTraceLogCacheItem = (): TraceLogCacheItem => ({
  loading: false,
  loaded: false,
  error: "",
  logs: [],
});

const createTaskCacheItem = (): TaskCacheItem => ({
  loading: false,
  loaded: false,
  error: "",
  tasks: [],
});

export function useAgentWorkbench(options: UseAgentWorkbenchOptions) {
  const activeDrawerKey = useState<string>(
    "active-agent-workbench-drawer",
    () => "",
  );
  const traceLogCache = useState<Record<string, TraceLogCacheItem>>(
    "agent-workbench-trace-log-cache",
    () => ({}),
  );
  const taskCache = useState<Record<string, TaskCacheItem>>(
    "agent-workbench-task-cache",
    () => ({}),
  );
  const activeTaskId = ref("");
  let taskRefreshTimer: ReturnType<typeof setInterval> | null = null;

  const normalizedTraceId = computed(() =>
    normalizeOptionalString(unref(options.traceId)),
  );
  const normalizedSessionId = computed(() =>
    normalizeOptionalString(unref(options.sessionId)),
  );
  const normalizedUserId = computed(() =>
    normalizeOptionalString(unref(options.userId)),
  );
  const drawerKey = computed(
    () =>
      normalizedTraceId.value ||
      normalizedSessionId.value ||
      "current-agent-workbench",
  );
  const drawerVisible = computed(
    () => activeDrawerKey.value === drawerKey.value,
  );
  const currentTraceLogState = computed(() =>
    getTraceLogCacheItem(drawerKey.value),
  );
  const currentTaskState = computed(() => getTaskCacheItem(drawerKey.value));

  const traceLoading = computed(() => currentTraceLogState.value.loading);
  const traceError = computed(() => currentTraceLogState.value.error);
  const logs = computed(() => currentTraceLogState.value.logs);
  const taskLoading = computed(() => currentTaskState.value.loading);
  const taskError = computed(() => currentTaskState.value.error);
  const tasks = computed(() => currentTaskState.value.tasks);
  const runningTasks = computed(() =>
    tasks.value.filter((task) => ACTIVE_TASK_STATUSES.has(task.status)),
  );
  const activeTask = computed(() => {
    const selectedTask = tasks.value.find((task) => task.id === activeTaskId.value);
    return selectedTask || runningTasks.value[0] || tasks.value[0];
  });
  const hasRunningTasks = computed(() => runningTasks.value.length > 0);

  const openDrawer = async () => {
    activeDrawerKey.value = drawerKey.value;
    await Promise.all([loadAgentTasks({ force: true }), loadTraceLogs()]);
  };

  const closeDrawer = () => {
    if (activeDrawerKey.value === drawerKey.value) {
      activeDrawerKey.value = "";
    }
  };

  /**
   * 获取轨迹日志缓存项
   * @param key
   * @returns
   */
  const loadTraceLogs = async () => {
    if (
      (!normalizedTraceId.value && !normalizedSessionId.value) ||
      currentTraceLogState.value.loading
    ) {
      return;
    }

    if (currentTraceLogState.value.loaded) {
      return;
    }

    updateTraceLogCacheItem(drawerKey.value, {
      loading: true,
      error: "",
    });

    try {
      const response = await $fetch<{
        success: boolean;
        logs: AgentTraceLog[];
      }>("/api/agent/traces", {
        query: buildQuery({
          traceId: normalizedTraceId.value,
          sessionId: normalizedSessionId.value,
          userId: normalizedUserId.value,
        }),
      });

      updateTraceLogCacheItem(drawerKey.value, {
        loaded: true,
        logs: response.success ? response.logs : [],
      });
    } catch (loadError) {
      updateTraceLogCacheItem(drawerKey.value, {
        loaded: true,
        error: getWorkbenchLoadError(loadError, "执行轨迹暂时不可用"),
        logs: [],
      });
    } finally {
      updateTraceLogCacheItem(drawerKey.value, {
        loading: false,
      });
    }
  };

  /**
   * 获取任务缓存项
   * @param key
   * @returns
   */
  const loadAgentTasks = async (options: LoadOptions = {}) => {
    if (
      (!normalizedTraceId.value && !normalizedSessionId.value) ||
      currentTaskState.value.loading
    ) {
      return;
    }

    if (currentTaskState.value.loaded && !options.force) {
      return;
    }

    const wasLoaded = currentTaskState.value.loaded;
    if (!wasLoaded) {
      updateTaskCacheItem(drawerKey.value, {
        loading: true,
        error: "",
      });
    } else {
      updateTaskCacheItem(drawerKey.value, {
        error: "",
      });
    }

    try {
      const response = await $fetch<{
        success: boolean;
        tasks: AgentTask[];
      }>("/api/agent/tasks", {
        query: buildQuery({
          traceId: normalizedTraceId.value,
          sessionId: normalizedSessionId.value,
          userId: normalizedUserId.value,
          limit: 5,
        }),
      });

      const taskDetails = await Promise.all(
        (response.success ? response.tasks : []).map((task) =>
          loadTaskDetail(task),
        ),
      );

      updateTaskCacheItem(drawerKey.value, {
        loaded: true,
        tasks: taskDetails,
      });
      ensureActiveTask(taskDetails);
    } catch (loadError) {
      updateTaskCacheItem(drawerKey.value, {
        loaded: true,
        error: getWorkbenchLoadError(loadError, "Agent 任务暂时不可用"),
        tasks: [],
      });
    } finally {
      if (!wasLoaded) {
        updateTaskCacheItem(drawerKey.value, {
          loading: false,
        });
      }
    }
  };

  const refreshAgentTasks = () => loadAgentTasks({ force: true });

  const setActiveTask = (taskId: string) => {
    activeTaskId.value = taskId;
  };

  const ensureActiveTask = (nextTasks: AgentTask[]) => {
    if (nextTasks.some((task) => task.id === activeTaskId.value)) {
      return;
    }

    const runningTask = nextTasks.find((task) => ACTIVE_TASK_STATUSES.has(task.status));
    activeTaskId.value = runningTask?.id || nextTasks[0]?.id || "";
  };

  const startTaskPolling = () => {
    if (!process.client || taskRefreshTimer || !drawerVisible.value || !hasRunningTasks.value) {
      return;
    }

    taskRefreshTimer = setInterval(() => {
      refreshAgentTasks();
    }, TASK_REFRESH_INTERVAL_MS);
  };

  const stopTaskPolling = () => {
    if (!taskRefreshTimer) {
      return;
    }

    clearInterval(taskRefreshTimer);
    taskRefreshTimer = null;
  };

  /**
   * 获取任务详情
   * @param task 
   * @returns 
   */
  const loadTaskDetail = async (task: AgentTask): Promise<AgentTask> => {
    try {
      const response = await $fetch<{
        success: boolean;
        task: AgentTask;
      }>("/api/agent/tasks", {
        query: buildQuery({
          taskId: task.id,
          userId: normalizedUserId.value,
        }),
      });

      return response.success ? response.task : task;
    } catch {
      return task;
    }
  };

  /**
   * 确认任务
   * @param taskId
   * @returns
   */
  const confirmTask = async (taskId: string) => {
    try {
      const response = await $fetch<{
        success: boolean;
        task?: AgentTask;
      }>("/api/agent/tasks/confirm", {
        method: "POST",
        body: {
          taskId,
          userId: normalizedUserId.value,
        },
      });

      if (response.success && response.task) {
        updateTaskCacheItem(drawerKey.value, {
          loaded: true,
          tasks: tasks.value.map((task) =>
            task.id === taskId ? (response.task as AgentTask) : task,
          ),
        });
      }
    } catch (confirmError) {
      const error = confirmError instanceof Error ? confirmError.message : "";
      updateTaskCacheItem(drawerKey.value, {
        error: error || "确认操作失败",
      });
    }
  };

  /**
   * 获取轨迹日志缓存项 
   * @param key 
   * @returns 
   */
  const getTraceLogCacheItem = (key: string): TraceLogCacheItem => {
    if (!traceLogCache.value[key]) {
      traceLogCache.value[key] = createTraceLogCacheItem();
    }

    return traceLogCache.value[key];
  };

  /**
   * 获取任务缓存项 
   * @param key 
   * @returns 
   */
  const getTaskCacheItem = (key: string): TaskCacheItem => {
    if (!taskCache.value[key]) {
      taskCache.value[key] = createTaskCacheItem();
    }

    return taskCache.value[key];
  };

  /**
   * 更新缓存项 
   * @param key 
   * @param patch 
   */
  const updateTraceLogCacheItem = (
    key: string,
    patch: Partial<TraceLogCacheItem>,
  ) => {
    traceLogCache.value[key] = {
      ...getTraceLogCacheItem(key),
      ...patch,
    };
  };

  /**
   * 获取任务缓存项 
   * @param key 
   * @returns 
   */
  const updateTaskCacheItem = (key: string, patch: Partial<TaskCacheItem>) => {
    taskCache.value[key] = {
      ...getTaskCacheItem(key),
      ...patch,
    };
  };

  watch(
    () => [normalizedTraceId.value, normalizedSessionId.value],
    () => {
      if (drawerVisible.value) {
        updateTraceLogCacheItem(drawerKey.value, { loaded: false });
        updateTaskCacheItem(drawerKey.value, { loaded: false });
        openDrawer();
      }
    },
  );

  watch([drawerVisible, hasRunningTasks], () => {
    if (drawerVisible.value && hasRunningTasks.value) {
      startTaskPolling();
      return;
    }

    stopTaskPolling();
  });

  onBeforeUnmount(() => {
    stopTaskPolling();
  });

  return {
    runningTasks,
    refreshAgentTasks,
    setActiveTask,
    drawerVisible, // 抽屉可见
    traceLoading, // 轨迹加载中
    traceError, // 轨迹错误
    logs, // 轨迹日志
    taskLoading, // 任务加载中
    taskError, // 任务错误
    tasks, // 任务列表
    activeTask, // 当前任务
    loadAgentTasks, // 预加载任务状态
    openDrawer, // 打开抽屉
    closeDrawer, // 关闭抽屉
    confirmTask, // 确认任务
  };
}
